import { useQuery, useZero } from "@rocicorp/zero/react";
import type { QueryRowType } from "@rocicorp/zero";
import { nanoid } from "nanoid";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useHasAccess } from "../hooks/useHasAccess";
import { useScopedModel } from "../hooks/useScopedModel";
import { markBranchAwaitingAiName } from "../lib/aiBranchNames";
import { clientSendContext } from "../lib/clientSendContext";
import { emitErrorToast } from "../lib/errorToast";
import { navigate, routes, useCurrentLocation } from "../lib/urls";
import { clearDraft, restoreDraft } from "../lib/draft";
import { saveQueue } from "./Chat/queueStorage";
import { Avatar, PlaceholderAvatar } from "./Avatar";
import { HelpTooltip } from "./HelpTooltip";
import { LabelWithInfo } from "./LabelWithInfo";
import { Text } from "./Text";
import { ChatActionPrompt } from "./Chat/ChatActionPrompt";
import type { ChatTool } from "./Chat/ChatTool";
import type { ChatProps } from "./Chat/chatTypes";
import { PAGE_SIZE } from "../hooks/useChatHistory";
import { useCursorChatHistory } from "../hooks/useCursorChatHistory";
import { ToolStatus } from "./Chat/ToolStatus";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { CHAT_WORKFLOWS_FLAG } from "../../shared/featureFlagNames";

export function LiveChatBranchQuestion({
  onAnswer,
  plan = false,
  disabled = false,
}: {
  onAnswer: (create: boolean) => Promise<boolean>;
  plan?: boolean;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  async function answer(create: boolean) {
    if (busy || disabled) return;
    setBusy(true);
    await onAnswer(create);
    setBusy(false);
  }
  return (
    <ChatActionPrompt
      title={
        plan
          ? "Create a branch to build this plan?"
          : "Create a branch from this conversation?"
      }
      confirmLabel={plan ? "Create branch and build" : "Create a branch"}
      confirmIcon="git-branch"
      cancelLabel={plan ? "Keep planning" : "Keep asking"}
      cancelIcon={plan ? "pencil" : "message-circle"}
      disabled={busy || disabled}
      onConfirm={() => void answer(true)}
      onCancel={() => void answer(false)}
    />
  );
}

export const requestBranch: ChatTool = {
  name: "requestBranch",
  alwaysLoadOutput: true,
  Header: ({ result }) => (
    <ToolStatus
      icon="git-branch"
      text={
        result
          ? (result.output as { created?: boolean })?.created
            ? "Continued in a branch"
            : "Kept private and read-only"
          : "Branch requested"
      }
    />
  ),
};
const suggestions = {
  header: "Ask about the live workflow",
  items: [
    {
      id: "explain",
      icon: "message-circle" as const,
      label: "Explain this workflow",
      prompt: "Explain how the live version of this workflow works.",
    },
    {
      id: "latest-run",
      icon: "message-circle" as const,
      label: "Check the latest run",
      prompt: "What happened in the latest run of the live version?",
    },
  ],
};

function showError() {
  emitErrorToast({
    icon: "x",
    message: "Couldn’t update the live conversation. Please try again.",
    buttonLabel: "Dismiss",
  });
}

type LiveChatRow = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadLiveChat>>
>["chat"];

function initialState(workflowId: string) {
  return {
    workflowId,
    started: false,
    building: false,
    newChatId: nanoid(12),
    closing: undefined as
      | { chat: LiveChatRow | undefined; cleared: boolean }
      | undefined,
    promotingChat: undefined as LiveChatRow | undefined,
    answeringBranchQuestion: false,
    failedBranchRequestId: undefined as string | undefined,
    continuedBranch: undefined as { id: string; chatId: string } | undefined,
  };
}

export function useLiveChat({
  workflowId,
  spaceId,
  liveBranchId,
  branchId,
  liveHistoryLabel,
  liveReviewVersion,
  enabled,
  entries,
  onLoadOlder,
  loadingOlder,
  onBuild,
}: {
  workflowId: string;
  spaceId: string;
  liveBranchId: string | undefined;
  branchId: string | undefined;
  liveHistoryLabel: string;
  liveReviewVersion?: ChatProps["reviewVersion"];
  enabled: boolean;
  entries: ChatProps["entries"];
  onLoadOlder?: () => void;
  loadingOlder?: boolean;
  onBuild?: ChatProps["onSend"];
}): { props?: Partial<ChatProps>; key?: string } {
  const z = useZero();
  const [currentUser] = useQuery(queries.currentUser());
  const briefed = useFeatureFlag(CHAT_WORKFLOWS_FLAG);
  const canCreateBranch = useHasAccess("space:write", spaceId);
  const { chatModel, chatModelReady } = useScopedModel(
    { type: "branch", branchId: liveBranchId ?? branchId ?? "" },
    { enabled }
  );
  const [state, setState] = useState(() => initialState(workflowId));
  if (state.workflowId !== workflowId) setState(initialState(workflowId));
  // Drops late async results from a workflow the user has since left.
  const set = (patch: Partial<ReturnType<typeof initialState>>) =>
    setState((s) => (s.workflowId === workflowId ? { ...s, ...patch } : s));
  const {
    started,
    building,
    newChatId,
    closing,
    promotingChat,
    answeringBranchQuestion,
    continuedBranch,
    failedBranchRequestId,
  } = state;
  const history = useCursorChatHistory(
    `${workflowId}:${newChatId}`,
    { type: "live", workflowId },
    !!liveBranchId
  );
  const liveChat = history.liveChat;
  if (promotingChat && continuedBranch && branchId === continuedBranch.id) {
    set({ promotingChat: undefined });
  }
  const queriedChat = liveChat?.chat;
  const chat =
    closing?.chat ??
    promotingChat ??
    (queriedChat ? { ...queriedChat, entries: history.entries } : undefined);
  const chatEntries =
    chat?.entries.toSorted(
      (a, b) =>
        a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
    ) ?? [];
  const chatId = chat?.id ?? newChatId;
  const busy = !!closing || !!promotingChat || answeringBranchQuestion;
  const pendingBranchQuestion = chat?.awaitingAction
    ? chatEntries.findLast(
        (entry) =>
          entry.entryType === "AssistantToolUse" &&
          (entry.toolCall?.toolName === "requestBranch" ||
            entry.toolCall?.toolName === "approvePlan") &&
          entry.toolCall.completedAt == null
      )
    : undefined;
  const pendingPlan =
    !chat?.respondingAt &&
    pendingBranchQuestion?.toolCall?.toolName === "approvePlan";
  const automaticBranchRequest =
    pendingBranchQuestion?.toolCall?.toolName === "requestBranch" &&
    (pendingBranchQuestion.toolCall.input as { automatic?: boolean } | null)
      ?.automatic === true;
  const failedBranchRequest =
    !!pendingBranchQuestion &&
    failedBranchRequestId === pendingBranchQuestion.id;
  const automaticBranchRequestId =
    enabled &&
    !building &&
    liveBranchId &&
    canCreateBranch &&
    chatModelReady &&
    !busy &&
    !failedBranchRequest &&
    !chat?.respondingAt &&
    automaticBranchRequest
      ? pendingBranchQuestion.id
      : undefined;
  const attemptedBranchRequestRef = useRef<string | undefined>(undefined);
  const draftKey = `live:${z.userID}:${workflowId}:${chatId}`;
  const { pathname } = new URL(useCurrentLocation(), location.origin);

  async function run(mutation: Parameters<typeof z.mutate>[0]) {
    const ok = await z.mutate(mutation).server.then(
      (r) => r.type !== "error",
      () => false
    );
    if (!ok) showError();
    return ok;
  }

  async function continueInBranch(
    branchId: string,
    commitId: string,
    branchChatId: string
  ) {
    // Matches the branch view’s own queries, so it opens with its transcript instead of an empty frame.
    const preloads = [
      z.preload(queries.loadBranchMetadata({ workflowId, branchId })),
      z.preload(queries.loadCommitChat({ commitId, entriesLimit: PAGE_SIZE })),
      z.preload(queries.loadCommitFileTree({ commitId })),
      z.preload(queries.loadBranchChats({ branchId, entriesLimit: 0 })),
      z.preload(
        queries.loadWorkflowChatEntries({
          chatIds: [branchChatId],
          branchId,
          commitId: undefined,
          after: undefined,
          from: undefined,
          limit: PAGE_SIZE,
        })
      ),
    ];
    // The branch already exists, so a failed preload must still let navigation proceed.
    await Promise.all(preloads.map(({ complete }) => complete.catch(() => {})));
    restoreDraft(
      `${pathname}:${branchId}`,
      localStorage.getItem(`draft:${pathname}:${draftKey}`) ?? ""
    );
    clearDraft(`${pathname}:${draftKey}`);
    markBranchAwaitingAiName(branchId);
    set({
      // Keyed by the live chat, so the conversation carries on into the branch without remounting.
      continuedBranch: { id: branchId, chatId },
      started: false,
      newChatId: nanoid(12),
    });
    navigate(routes.space.workflow, {
      spaceId,
      workflowId,
      branchId,
      commitId: "",
    });
    for (const { cleanup } of preloads) cleanup();
  }

  // Another tab answered first; its answer records the branch it created.
  async function continueExistingBranch(entryId: string | undefined) {
    if (!entryId) return false;
    const live = await z.run(queries.loadLiveChat({ workflowId }), {
      type: "complete",
    });
    const request = live?.chat?.entries.find((entry) => entry.id === entryId);
    const branchId = (
      request?.toolCall?.output as { branchId?: string | null } | null
    )?.branchId;
    if (!branchId) return false;
    const workflow = await z.run(queries.loadWorkflow({ workflowId }), {
      type: "complete",
    });
    const branch = workflow?.branches.find((branch) => branch.id === branchId);
    const branchChatId = branch?.commits[0]?.chatId;
    if (!branch?.headCommitId || !branchChatId) return false;
    await continueInBranch(branch.id, branch.headCommitId, branchChatId);
    return true;
  }

  async function buildWithZip() {
    if (busy || !canCreateBranch || !onBuild) return;
    const branchId = nanoid(12);
    const commitId = nanoid(12);
    const branchChatId = nanoid(12);
    set({ building: true, answeringBranchQuestion: true });
    try {
      const created = await run(
        sharedMutators.createBranch({
          workflowId,
          branchId,
          commitId,
          chatId: branchChatId,
          name: "New branch",
        })
      );
      if (created) await continueInBranch(branchId, commitId, branchChatId);
    } catch {
      showError();
    }
    set({ answeringBranchQuestion: false });
  }

  async function dismiss() {
    if (busy) return;
    set({ closing: { chat, cleared: false } });
    if (!(await run(sharedMutators.clearLiveChat({ workflowId, chatId })))) {
      set({ closing: undefined });
      return;
    }
    clearDraft(`${pathname}:${draftKey}`);
    saveQueue(chatId, []);
    set({ closing: { chat, cleared: true } });
  }

  async function answer(create: boolean, entryId?: string, chatModel?: string) {
    if (busy || (create && (!canCreateBranch || !chatModelReady))) return false;
    const branchId = nanoid(12);
    const commitId = nanoid(12);
    const branchChatId = nanoid(12);
    set({ answeringBranchQuestion: true });
    if (create) set({ promotingChat: chat, failedBranchRequestId: undefined });
    try {
      const result = await z.mutate(
        sharedMutators.answerLiveChatBranchRequest({
          workflowId,
          chatId,
          entryId,
          createBranch: create,
          branchId,
          commitId,
          temporaryChatId: branchChatId,
          kickoffEntryId: nanoid(12),
          chatModel,
        })
      ).server;
      if (result.type === "error") {
        if (create && (await continueExistingBranch(entryId))) {
          set({ answeringBranchQuestion: false });
          return true;
        }
        set({
          answeringBranchQuestion: false,
          promotingChat: undefined,
          failedBranchRequestId: create ? entryId : undefined,
        });
        showError();
        return false;
      }
      if (create) await continueInBranch(branchId, commitId, branchChatId);
      set({ answeringBranchQuestion: false });
      return true;
    } catch {
      set({
        answeringBranchQuestion: false,
        promotingChat: undefined,
        failedBranchRequestId: create ? entryId : undefined,
      });
      showError();
      return false;
    }
  }

  async function answerBranchQuestion(create: boolean) {
    if (pendingBranchQuestion)
      return answer(create, pendingBranchQuestion.id, chatModel ?? undefined);
    if (!create || !chat || chat.respondingAt || !canCreateBranch) return false;
    return answer(true, undefined, chatModel ?? undefined);
  }

  const continueRequestedBranch = useEffectEvent((entryId: string) => {
    void answer(true, entryId, chatModel ?? undefined);
  });
  useEffect(() => {
    if (
      !automaticBranchRequestId ||
      attemptedBranchRequestRef.current === automaticBranchRequestId
    )
      return;
    attemptedBranchRequestRef.current = automaticBranchRequestId;
    continueRequestedBranch(automaticBranchRequestId);
  }, [automaticBranchRequestId]);

  if (!enabled || !liveBranchId)
    return {
      key:
        branchId === continuedBranch?.id ? continuedBranch?.chatId : undefined,
    };

  if (building) {
    return {
      key: continuedBranch?.chatId ?? chatId,
      props: {
        draftKey,
        writeChatId: null,
        streamChatId: null,
        onZipFile: undefined,
        acceptAnyFile: canCreateBranch,
        canWrite: history.ready && !busy && canCreateBranch,
        respondingAt: null,
        awaitingAction: false,
        placeholder: "Ask AI to build",
        onSend: (payload, onError) => {
          if (busy || !canCreateBranch || !onBuild) {
            onError();
            return;
          }
          onBuild(payload, onError);
        },
      },
    };
  }

  const hasConversation = started || !!chat?.entries.length;
  return {
    key: chatId,
    props: {
      entries: hasConversation ? chatEntries : entries,
      loadingOlder: hasConversation
        ? !busy && history.loadingOlder
        : loadingOlder,
      onLoadOlder: hasConversation
        ? !busy
          ? history.onLoadOlder
          : undefined
        : onLoadOlder,
      reviewVersion: hasConversation ? undefined : liveReviewVersion,
      liveConversation: undefined,
      writeChatId: chatId,
      streamChatId: chat?.id ?? null,
      draftKey,
      acceptAnyFile: false,
      onZipAttachment:
        canCreateBranch && !busy && onBuild
          ? () => void buildWithZip()
          : undefined,
      onSend: (payload, onError) => {
        if (busy) {
          onError();
          return;
        }
        set({ started: true });
        void run(
          sharedMutators.sendLiveChatMessage({
            workflowId,
            chatId,
            entryId: nanoid(12),
            text: payload.text,
            attachments: payload.attachments,
            isDictated: payload.isDictated,
            planMode: payload.planMode,
            effort: payload.effort,
            chatModel: payload.chatModel,
            interruptEntryId: payload.interruptEntryId,
            ...clientSendContext(),
          })
        ).then((ok) => {
          if (ok) return;
          onError();
          set({ started: false });
        });
      },
      onInterrupt: () => {
        if (busy) return;
        void run(
          sharedMutators.interruptLiveChat({
            workflowId,
            chatId,
            entryId: nanoid(12),
          })
        );
      },
      onProvideToolResult: async (entryId, output, _isError, options) => {
        const created = (output as { created?: boolean })?.created;
        if (typeof created !== "boolean") return false;
        return answer(created, entryId, options?.chatModel);
      },
      canWrite: history.ready && !busy,
      suggestions,
      respondingAt: chat?.respondingAt ?? null,
      lastLoopHeartbeatAt: chat?.lastLoopHeartbeatAt,
      awaitingAction: !closing && (chat?.awaitingAction ?? false),
      pendingActionStatus:
        promotingChat ||
        (automaticBranchRequest && canCreateBranch && !failedBranchRequest)
          ? "building"
          : "waiting",
      pendingPromptOverride:
        !busy &&
        !chat?.respondingAt &&
        pendingBranchQuestion &&
        !canCreateBranch ? (
          <ChatActionPrompt
            title="You need edit access to create a branch."
            confirmLabel={pendingPlan ? "Keep planning" : "Keep asking"}
            confirmIcon="message-circle"
            cancelLabel="Close chat"
            cancelIcon="x"
            onConfirm={() => void answerBranchQuestion(false)}
            onCancel={() => void dismiss()}
          />
        ) : !busy && failedBranchRequest ? (
          <ChatActionPrompt
            title="Couldn’t create a branch. Your conversation is still private."
            confirmLabel="Try again"
            confirmIcon="git-branch"
            cancelLabel={pendingPlan ? "Keep planning" : "Keep asking"}
            cancelIcon="message-circle"
            disabled={!chatModelReady}
            onConfirm={() => void answerBranchQuestion(true)}
            onCancel={() => void answerBranchQuestion(false)}
          />
        ) : !busy &&
          !chat?.respondingAt &&
          pendingBranchQuestion &&
          !automaticBranchRequest ? (
          <LiveChatBranchQuestion
            plan={pendingPlan}
            disabled={!chatModelReady}
            onAnswer={answerBranchQuestion}
          />
        ) : undefined,
      placeholder: "Ask about the live workflow",
      topOverlay: undefined,
      exploratoryConversation: {
        live: {
          id: liveBranchId,
          label: liveHistoryLabel,
          entries,
          onLoadOlder,
          loadingOlder,
          reviewVersion: liveReviewVersion,
        },
        onCloseComplete: closing?.cleared
          ? () =>
              set({ newChatId: nanoid(12), started: false, closing: undefined })
          : undefined,
        banner: hasConversation
          ? {
              show: true,
              disabled: busy,
              icon: currentUser ? (
                <Avatar user={currentUser} size={16} tooltip={false} />
              ) : (
                <PlaceholderAvatar size={16} />
              ),
              message: (
                <LabelWithInfo>
                  <Text variant="text-regular">New chat</Text>
                  <HelpTooltip
                    align="center"
                    content={
                      briefed
                        ? "Ask about the live workflow, its runs, and output data privately. Asking for changes starts a shared branch from a brief; this conversation stays private. Closing it clears the conversation."
                        : "Ask about the live workflow, its runs, and output data privately. Asking for changes moves this conversation into a shared branch. Closing it clears the conversation."
                    }
                  />
                </LabelWithInfo>
              ),
              buttonLabel: "Close chat",
              onButtonClick: () => void dismiss(),
              secondaryAction: canCreateBranch
                ? {
                    label: promotingChat
                      ? "Creating branch…"
                      : "Continue in branch",
                    icon: "git-branch",
                    disabled:
                      !!promotingChat ||
                      !chatModelReady ||
                      !!chat?.respondingAt ||
                      !chat?.entries.length,
                    tooltip: chat?.respondingAt
                      ? "Wait for the reply to finish"
                      : undefined,
                    onClick: () => void answerBranchQuestion(true),
                  }
                : undefined,
            }
          : undefined,
      },
    },
  };
}
